import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { OrderQuery } from '@modules/order/state/order-query';
import { OrderState, OrderStore } from '@modules/order/state/order-store';
import { ProcedureConfigurationService } from '@modules/order/services/procedure-configuration.service';
import { ProcedureFormStateService } from '@shared/services/procedure-form-state.service';
import { OrderFacadeBase } from '@modules/order/order.facade.base';
import { ShellStore } from '@shared/store/shell/shell-store';
import { SendToOption } from '@shared/models/send-to-option';
import { ProcedureTilesService } from '@modules/rx-intuitive/services/procedure-tiles.service';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { RxIntuitiveStoresResetService } from '@shared/services/rx-intuitive-stores-reset.service';
import { RxIntuitiveTeethDiagramQuery } from '@modules/rx-intuitive-teeth-orchestrator/state/rx-intuitive-teeth-diagram-query';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { OrderFormConfirmationService } from '@modules/order/services/order-form-confirmation.service';
import { getAutoSelectionPriority, isAutoSelectableLab } from '@shared/utils/send-to-utils';
import { ScanLayersLockService } from '@shared/services/scan-layers-lock.service';
import { ProcedureTypeService } from '@shared/components/rx-procedure-type-control/procedure-type.service';
import { RxTemplateDirtyCheckService } from '@shared/services/rx-template-dirty-check.service';

@Injectable()
export class RxIntuitiveOrderFacade extends OrderFacadeBase {
	procedureMap$ = this.orderQuery.procedureMap$;
	isReadOnly$ = this.shellQuery.getReadOnlyState('order');
	// Host read-only rule, a multilayer scan added, or a duplicated case (all lock procedure selection).
	isProcedureSelectionReadOnly$ = combineLatest([
		this.shellQuery.getReadOnlyOrderState('procedureSelection'),
		this.scanLayersLockService.isScanLayersLocked$,
		this.scanLayersLockService.isDuplicatedCaseLocked$
	]).pipe(map(([isReadOnly, isScanLayersLocked, isDuplicatedCaseLocked]) => isReadOnly || isScanLayersLocked || isDuplicatedCaseLocked));
	isReturned$ = this.shellQuery.isReturned$;
	defaultProcedure$ = this.procedureConfigurationService.defaultProcedure$;

	sendToState$ = combineLatest([
		this.orderQuery.procedureMap$,
		this.shellQuery.shouldValidateForSend$,
		this.shellQuery.isResendScan$
	]).pipe(
		map(([pm, shouldValidateForSend, isResendScan]) =>
			this.procedureFormStateService.getSendToState(pm, shouldValidateForSend, isResendScan)
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	dueDateState$ = combineLatest([this.orderQuery.procedureMap$, this.isReadOnly$, this.rxTemplateService.template$]).pipe(
		map(([pm, isReadOnly, template]) => this.procedureFormStateService.getDueDateState(pm, isReadOnly, template)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	isSendToCancellable$ = combineLatest([this.orderQuery.procedureMap$, this.orderQuery.availableProcedureMaps$]).pipe(
		map(([pm, availableProcedureMaps]) => this.procedureFormStateService.isSendToCancellable(pm, availableProcedureMaps)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	procedureTiles$ = this.procedureTilesService.procedureTiles$;
	availableLabs$ = this.procedureConfigurationService.procedureSendToOptions$;
	minimalAvailableDueDate$ = this.orderQuery.minimalAvailableDueDate$;
	sendTo$ = this.orderQuery.sendTo$;
	dueDate$ = this.orderQuery.dueDate$;
	userRole$ = this.shellQuery.userRole$;

	selectedProcedureId$ = this.procedureMap$.pipe(
		map(procedureMap => procedureMap?.ProcedureId),
		distinctUntilChanged(),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	selectedProcedureTile$ = combineLatest([this.procedureTiles$, this.selectedProcedureId$]).pipe(
		distinctUntilChanged(),
		map(([procedureTiles, selectedProcedureId]) =>
			selectedProcedureId ? procedureTiles.find(tile => tile.Id === selectedProcedureId) : undefined
		)
	);

	get procedure() {
		return this.orderQuery.procedure;
	}

	constructor(
		protected readonly orderStore: OrderStore,
		protected readonly orderQuery: OrderQuery,
		protected readonly shellStore: ShellStore,
		protected readonly shellQuery: ShellQuery,
		protected readonly logger: LoggerService,
		private readonly procedureConfigurationService: ProcedureConfigurationService,
		private readonly procedureTilesService: ProcedureTilesService,
		private readonly procedureFormStateService: ProcedureFormStateService,
		private readonly rxStoresResetService: RxIntuitiveStoresResetService,
		private readonly intuitiveDiagramQuery: RxIntuitiveTeethDiagramQuery,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly rxTemplateService: RxTemplateService,
		private readonly orderFormConfirmationService: OrderFormConfirmationService,
		private readonly scanLayersLockService: ScanLayersLockService,
		private readonly procedureTypeService: ProcedureTypeService,
		private readonly rxTemplateDirtyCheckService: RxTemplateDirtyCheckService
	) {
		super(orderStore, orderQuery, shellQuery, logger);
	}

	handleProcedureChanged(procedureId: number): void {
		const currentProcedureId = this.orderQuery.procedureMap?.ProcedureId;

		if (currentProcedureId === procedureId) {
			return;
		}

		this.rxStoresResetService.resetDependingStores();

		const autoSelectedSendTo = this.resolveAutoSelectedSendTo(procedureId);

		// Resolve the procedure map together with the auto-selected send-to so the stored map matches
		// the lab's send-to type (e.g. Heartland requires the resto-lab map, not the empty-lab map).
		const procedureMap = this.procedureConfigurationService.getProcedureMap(procedureId, undefined, autoSelectedSendTo);

		this.orderStore.update({
			procedureMap,
			sendTo: autoSelectedSendTo,
			dueDate: null,
			isMultiBiteSelected: undefined
		});
	}

	handleSendToChanged(sendTo: SendToOption): Observable<SendToOption | null> {
		if (sendTo?.Id === this.orderQuery.sendTo?.Id) {
			return of(sendTo);
		}

		const procedureMapPatch = this.getProcedureMapPatch(sendTo);
		const isProcedureTypeChanged =
			!!procedureMapPatch.procedureMap && procedureMapPatch.procedureMap.TypeId !== this.orderQuery.procedureMap?.TypeId;
		const requiresConfirmation = this.requiresSendToChangeConfirmation(
			sendTo,
			procedureMapPatch.procedureMap?.ProcedureId,
			isProcedureTypeChanged
		);

		// Skip the async dirty-data check when requiresConfirmationalready triggers the dialog.
		const dirtyHiddenData$ = requiresConfirmation
			? of(false)
			: this.rxTemplateDirtyCheckService.shouldConfirmFor$(
					sendTo,
					procedureMapPatch?.procedureMap ?? this.orderQuery.procedureMap,
					'intuitive'
				);

		return dirtyHiddenData$.pipe(
			switchMap(hasDirtyHiddenData => {
				const shouldShowDialog = hasDirtyHiddenData || requiresConfirmation;

				const isConfirmed$ = shouldShowDialog
					? this.orderFormConfirmationService.openSendToChangePopup$().pipe(map(result => result === PopUpActions.Ok))
					: of(true);

				return isConfirmed$.pipe(
					map(isConfirmed => {
						if (!isConfirmed) {
							return this.orderQuery.sendTo;
						}

						if (requiresConfirmation) {
							this.rxStoresResetService.resetDependingStores();
						}
						if (isProcedureTypeChanged) {
							this.procedureTypeService.resetStoresAfterProcedureTypeChange();
						}
						this.orderStore.update({ ...procedureMapPatch, sendTo });

						// Reset restorative multi-bite when the new lab's template disables the toggle.
						const isMultiBiteDisabledByNewTemplate =
							!!this.rxTemplateService.resolveTemplate(sendTo).ui.scanOptions?.items.multiBite.disabled;

						if (isMultiBiteDisabledByNewTemplate) {
							this.rxStoresResetService.resetRestoMultiBite();
						}

						return sendTo;
					})
				);
			})
		);
	}

	handleDueDateChanged(dueDate: Date): void {
		this.orderStore.update({ dueDate });
	}

	/** Procedure map matching the new send-to, or an empty patch when the current procedure map stays. */
	private getProcedureMapPatch(sendTo: SendToOption | null): Pick<Partial<OrderState>, 'procedureMap'> {
		const currentProcedureMap = this.orderQuery.procedureMap;

		if (currentProcedureMap?.ProcedureId == null) {
			return {};
		}

		const procedureMap = this.procedureConfigurationService.getProcedureMapForSendTo(currentProcedureMap, sendTo);

		return !procedureMap || procedureMap.Id === currentProcedureMap.Id ? {} : { procedureMap };
	}

	/**
	 * The send-to change dialog is shown only when the teeth diagram has unsaved work that is going to be reset:
	 * either the target template requires it, or the send-to forces a procedure type change the teeth depend on.
	 */
	private requiresSendToChangeConfirmation(
		sendTo: SendToOption,
		procedureId: number | undefined,
		isProcedureTypeChanged: boolean
	): boolean {
		if (!this.teethDiagramQuery.isDirty && !this.intuitiveDiagramQuery.isDirty) {
			return false;
		}

		const isRequiredByTemplate =
			!!this.rxTemplateService.resolveTemplate(sendTo).ui.order.items.sendToChange.requiresConfirmationForReset;
		const isRequiredByTypeChange = isProcedureTypeChanged && this.procedureTypeService.requiresTeethResetOnTypeChange(procedureId);

		return isRequiredByTemplate || isRequiredByTypeChange;
	}

	private resolveAutoSelectedSendTo(procedureId: number): SendToOption | null {
		const autoSelectableLabs = (this.procedureConfigurationService.getAvailableLabs({ procedureId }) ?? []).filter(isAutoSelectableLab);

		return autoSelectableLabs.reduce<SendToOption | null>(
			(best, lab) => (!best || (getAutoSelectionPriority(lab) ?? 0) > (getAutoSelectionPriority(best) ?? 0) ? lab : best),
			null
		);
	}
}
